import { createFileRoute } from "@tanstack/react-router";
import { Code2, Plug, Puzzle } from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import { alternateLinkList } from "@/lib/i18n/locale";
import heroImg from "@/assets/integrations-hero.webp";
import syncImg from "@/assets/integrations-sync.webp";
import apiImg from "@/assets/integrations-api.webp";

const IMG: Record<string, string> = { hero: heroImg, sync: syncImg, api: apiImg };

export const Route = createFileRoute("/vi/platform/integrations")({
  head: () => ({
    meta: [
      { title: "Tích hợp — banana.cards" },
      { name: "description", content: "Native, Zapier, n8n và API mở. Kết nối Banana với mọi công cụ bạn đã dùng." },
      { property: "og:title", content: "Tích hợp — banana.cards" },
      { property: "og:description", content: "Native, Zapier, n8n và API mở." },
      { property: "og:image", content: IMG['hero'] },
      { property: "og:locale", content: "vi_VN" },
    ],
    links: alternateLinkList("/platform/integrations", "vi"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="Tích hợp"
      title="Hoạt động với"
      italic="mọi công cụ của bạn."
      description="Tích hợp native với các công cụ phổ biến nhất, cộng Zapier, n8n và API mở. Banana là trung tâm — không phải hòn đảo."
      heroImage={IMG['hero']}
      metrics={[{ v: "50+", l: "tích hợp native" }, { v: "5000+", l: "qua Zapier" }, { v: "REST", l: "+ Webhooks" }]}
      bullets={[
        { icon: Plug, title: "Native", desc: "Slack, Google Workspace, Microsoft 365, HubSpot, Salesforce, Stripe và hơn." },
        { icon: Puzzle, title: "Không-code", desc: "Zapier, Make, n8n — hơn 5000 app trong vài click." },
        { icon: Code2, title: "API mở", desc: "REST API và webhook cho mọi đối tượng — xây gì bạn muốn." }
      ]}
      deepDives={[
        { eyebrow: "Đồng bộ", title: "Đồng bộ 2 chiều,", italic: "luôn cập nhật.", description: "Liên hệ, deal và task đồng bộ tự động giữa Banana và các công cụ của bạn — không cần copy thủ công.", image: IMG["sync"], reverse: false, points: ["Đồng bộ 2 chiều liên hệ", "Mapping trường tùy chỉnh", "Đồng bộ theo lịch hoặc thời gian thực", "Phát hiện và xử lý xung đột"] },
        { eyebrow: "API", title: "Một API,", italic: "vô hạn khả năng.", description: "REST API và webhook cho mọi đối tượng. Lý tưởng cho integration tùy chỉnh, app nội bộ và workflow nâng cao.", image: IMG["api"], reverse: true, points: ["REST API có tài liệu đầy đủ", "Webhook cho mọi sự kiện", "OAuth & API key", "Rate limit & SDK"] }
      ]}
      howItWorks={[
        { title: "Chọn integration", desc: "Native hoặc qua Zapier." },
        { title: "Đăng nhập", desc: "OAuth một-click cho hầu hết." },
        { title: "Map trường", desc: "Chọn dữ liệu để đồng bộ." },
        { title: "Bật & theo dõi", desc: "Log đồng bộ trong dashboard." }
      ]}
      capabilities={["50+ integration native", "5000+ qua Zapier/Make/n8n", "REST API đầy đủ", "Webhook cho mọi sự kiện", "OAuth & API key", "Mapping trường tùy chỉnh", "Đồng bộ thời gian thực hoặc theo lịch", "SDK cho JavaScript và Python"]}
      faq={[
        { q: "Banana tích hợp với những công cụ nào?", a: "Native: Slack, Google Workspace, Microsoft 365, HubSpot, Salesforce, Stripe, Mailchimp, Notion, Airtable, Zoom, Teams. Qua Zapier/Make/n8n: 5000+ app khác." },
        { q: "Có API mở không?", a: "Có — REST API đầy đủ, có tài liệu, với webhook cho mọi sự kiện. Lý tưởng cho integration tùy chỉnh." },
        { q: "Đồng bộ là 2 chiều không?", a: "Có cho hầu hết integration — thay đổi ở Banana đẩy ra công cụ kia và ngược lại, với phát hiện xung đột." },
        { q: "Có giới hạn rate không?", a: "Có — rate limit hợp lý cho REST API (1000 req/phút trên gói cao cấp). Webhook không có giới hạn cho sự kiện outbound." }
      ]}
      outro={{ title: "Banana ở trung tâm.", italic: "Phần còn lại theo sau.", description: "Tích hợp khiến công cụ của bạn hoạt động như một." }}
    />
  );
}